import { Capacitor } from '@capacitor/core';
import api from '../api';

const FCM_STORAGE_KEY = 'nmhelper_fcm_token';
let listenersAttached = false;

export function getStoredFcmToken() {
  try {
    return localStorage.getItem(FCM_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function storeFcmToken(token) {
  try {
    if (token) localStorage.setItem(FCM_STORAGE_KEY, token);
    else localStorage.removeItem(FCM_STORAGE_KEY);
  } catch {
    // ignore
  }
}

async function saveTokenToServer(fcmToken, { useMalayalam = false } = {}) {
  if (!fcmToken) return;
  await api.post('/device-token', {
    fcm_token: fcmToken,
    platform: Capacitor.getPlatform(),
    locale: useMalayalam ? 'ml' : 'en',
  });
  storeFcmToken(fcmToken);
}

export async function unregisterPushToken() {
  const fcmToken = getStoredFcmToken();
  try {
    if (fcmToken) {
      await api.delete('/device-token', { data: { fcm_token: fcmToken } });
    } else {
      await api.delete('/device-token', { data: {} });
    }
  } catch {
    // best effort — user may already be logged out
  }
  storeFcmToken('');
}

/**
 * Register for FCM on native Android. Push tokens stay on the server until Sign out.
 * Does not require daily re-login (JWT is long-lived for head/teacher).
 */
export async function registerPushNotifications({ useMalayalam = false } = {}) {
  if (!Capacitor.isNativePlatform()) return;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  if (!listenersAttached) {
    listenersAttached = true;

    PushNotifications.addListener('registration', async (token) => {
      try {
        await saveTokenToServer(token.value, { useMalayalam });
      } catch (err) {
        console.warn('Failed to save FCM token:', err);
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('Push registration error:', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      window.dispatchEvent(
        new CustomEvent('nmhelper:push-received', {
          detail: {
            title: notification.title,
            body: notification.body,
          },
        })
      );
    });
  }

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return;

  await PushNotifications.register();

  const existing = getStoredFcmToken();
  if (existing) {
    try {
      await saveTokenToServer(existing, { useMalayalam });
    } catch {
      // will retry on registration event
    }
  }
}
